import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import 'es6-promise/auto';
import GoogleMapReact from 'google-map-react';
import { getAdditionalProps } from '@eb/eds-utils';

import {
    CENTER_OF_THE_WORLD,
    COORDINATE_SHAPE,
    DEFAULT_ZOOM,
    DIMENSIONS_TYPE,
    ELEMENT_TYPES,
    LOCAL_MAP_OPTIONS,
    MAP_TYPE_LOCAL,
    MAP_TYPE_WORLD,
    MAP_TYPES,
    WORLD_MAP_OPTIONS,
} from './constants';

import './interactiveMap.scss';

export default class InteractiveMap extends PureComponent {
    static propTypes = {
        /**
         * API Key for requests to Google Maps API. Can be used instead of a client param.
         */
        googleMapsApiKey: PropTypes.string,
        /**
         * Client param to pass to Google Maps API. Can be used instead of an API key.
         */
        googleMapsClient: PropTypes.string,
        /**
         * Which of the defined map styles to use.
         */
        mapType: PropTypes.oneOf(MAP_TYPES),
        /**
         * Height for the map container, with units
         */
        height: DIMENSIONS_TYPE,
        /**
         * Width for the map container, with units
         */
        width: DIMENSIONS_TYPE,
        /**
         * Two-letter language code
         */
        language: PropTypes.string,
        /**
         * Two-letter region code
         */
        region: PropTypes.string,
        /**
         * Default map zoom level - changing this prop throws a warning
         */
        defaultZoom: PropTypes.number,
        /**
         * Zoom level
         */
        zoom: PropTypes.number,
        /**
         * Function that is called when the bounds change
         * ({ center, zoom, bounds, marginBounds })
         */
        onChange: PropTypes.func,
        /**
         * Function that is called when the map is clicked
         * ({ x, y, lat, lng, event })
         */
        onClick: PropTypes.func,
        /**
         * Function that is called when a child of the map is clicked
         */
        onChildClick: PropTypes.func,
        /**
         * Function that is called when the visible tiles have finished loading
         */
        onTilesLoaded: PropTypes.func,
        /**
         * Function that is called when zoom animation starts
         */
        onZoomAnimationStart: PropTypes.func,
        /**
         * Function that is called at the end of the zoom animation
         */
        onZoomAnimationEnd: PropTypes.func,
        /**
         * Options to override default options. To be used sparingly. */
        // eslint-disable-next-line react/forbid-prop-types
        options: PropTypes.object,
        /**
         * One or more children to display on the map that will be positioned
         * according to their lat and lng props.
         */
        children: ELEMENT_TYPES,
        /**
         * The default center point of the portion of the map to be shown. Should not change.
         */
        defaultCenter: COORDINATE_SHAPE,
        /**
         * The center point of the portion of the map to be shown.
         */
        center: COORDINATE_SHAPE,
        /**
         * Whether the bounds for the map should be reset on window resize
         */
        resetBoundsOnResize: PropTypes.bool,
    };

    static defaultProps = {
        defaultCenter: CENTER_OF_THE_WORLD,
        defaultZoom: DEFAULT_ZOOM,
        language: 'en',
        mapType: MAP_TYPE_WORLD,
        region: 'us',
    };

    _getMapOptions = (mapType, options) => ({
        ...(mapType === MAP_TYPE_LOCAL ? LOCAL_MAP_OPTIONS : WORLD_MAP_OPTIONS),
        ...options,
    });

    render() {
        const {
            center,
            children,
            defaultCenter,
            defaultZoom,
            googleMapsApiKey,
            googleMapsClient,
            height,
            language,
            mapType,
            onChange,
            onChildClick,
            onClick,
            onTilesLoaded,
            options,
            region,
            resetBoundsOnResize,
            width,
            zoom,
        } = this.props;

        // Make it so that the `key` and `client` properties will not
        // be on the bootstrapURLKeys object if the value is falsy
        const bootStrapURLKeysKey = googleMapsApiKey
            ? { key: googleMapsApiKey }
            : {};
        const bootStrapURLKeysClient = googleMapsClient
            ? { client: googleMapsClient }
            : {};

        const extraAttrs = getAdditionalProps(this);

        return (
            <div className="interactive-map" style={{ width, height }}>
                <GoogleMapReact
                    bootstrapURLKeys={{
                        ...bootStrapURLKeysKey,
                        ...bootStrapURLKeysClient,
                        language,
                        region,
                    }}
                    defaultCenter={defaultCenter}
                    center={center}
                    defaultZoom={defaultZoom}
                    zoom={zoom}
                    onChange={onChange}
                    onChildClick={onChildClick}
                    onClick={onClick}
                    onZoomAnimationEnd={this._handleZoomAnimationEnd}
                    onZoomAnimationStart={this._handleZoomAnimationStart}
                    resetBoundsOnResize={resetBoundsOnResize}
                    options={this._getMapOptions(mapType, options)}
                    onTilesLoaded={onTilesLoaded}
                    {...extraAttrs}
                >
                    {children}
                </GoogleMapReact>
            </div>
        );
    }
}
